import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import React, { useEffect, Suspense } from "react";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TabProvider } from './contexts/TabContext';
import { EmailComposerProvider } from './contexts/EmailComposerContext';
import { WindowManagerProvider } from './contexts/WindowManagerContext';
import { ChatProvider } from './contexts/ChatContext';
import { AufgabenFensterProvider, AufgabenFensterAnzeige } from './contexts/AufgabenFensterContext';
import { TerminFensterProvider, TerminFensterAnzeige } from './contexts/TerminFensterContext';
import FloatingChatButton from './components/chat/FloatingChatButton';
import CookieConsentBanner from './components/CookieConsentBanner';
import DeployOverlay from './components/DeployOverlay';
import SitzungGewechseltHinweis from './components/SitzungGewechseltHinweis';
import NewVersionNotice from './components/NewVersionNotice';
import { isChunkLoadError, canAutoReload, reloadWhenReady } from './utils/versionReload';
import { initDraftManager } from './utils/draftManager';

// Zentraler Entwurfs-Manager – schützt vor Datenverlust bei Deploy/Reload/Tab-Close.
initDraftManager();
import { Layout } from './components/layout/Layout';
import './i18n';
import "./App.css";

// ─── Lazy-loaded Pages (Code-Splitting) ───
// Named exports: .then(m => ({ default: m.X }))
// Default exports: direct import

// Public Pages
const LandingPage = React.lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
// Neue Website – seit 11.10.2026 Startseite (/, /preise, /branchen/:slug); alte Startseite unter /alt
const NeuStartseite = React.lazy(() => import('./pages/neu/NeuStartseite'));
const NeuPreise = React.lazy(() => import('./pages/neu/NeuPreise'));
const NeuBranche = React.lazy(() => import('./pages/neu/NeuBranche'));
const LoginPage = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const DemoPage = React.lazy(() => import('./pages/DemoPage').then(m => ({ default: m.DemoPage })));
const FussballLandingpage = React.lazy(() => import('./pages/FussballLandingpage'));
const StudentBranchenseite = React.lazy(() => import('./pages/StudentBranchenseite'));
const ImprintPage = React.lazy(() => import('./pages/ImprintPage').then(m => ({ default: m.ImprintPage })));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = React.lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const AVVPage = React.lazy(() => import('./pages/AVVPage').then(m => ({ default: m.AVVPage })));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const SuperAdminLoginPage = React.lazy(() => import('./pages/SuperAdminLoginPage'));
const SuperAdminResetPasswordPage = React.lazy(() => import('./pages/SuperAdminResetPasswordPage'));
const SuperAdminPageMobileAware = React.lazy(() => import('./pages/SuperAdminPageMobileAware'));
const DocumentationLoginPage = React.lazy(() => import('./pages/DocumentationLoginPage'));
const PublicBookingPage = React.lazy(() => import('./pages/PublicBookingPage'));
const PublicReservationPage = React.lazy(() => import('./pages/PublicReservationPage'));
const PublicMenuPage = React.lazy(() => import('./pages/PublicMenuPage'));
const PublicTournamentPage = React.lazy(() => import('./components/public/PublicTournamentPage'));
const TournamentScoreboard = React.lazy(() => import('./components/public/TournamentScoreboard'));

// Portal
const PortalLayout = React.lazy(() => import('./pages/portal/PortalLayout'));

// Protected Pages
const Dashboard = React.lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AddressesPage = React.lazy(() => import('./pages/AddressesPage').then(m => ({ default: m.AddressesPage })));
const MobileAddressesPage = React.lazy(() => import('./pages/MobileAddressesPage'));
const AddressesPageMobileAware = React.lazy(() => import('./pages/AddressesPageMobileAware'));
const AdressenPapierkorbPage = React.lazy(() => import('./pages/AdressenPapierkorbPage'));
const AddressDetailPage = React.lazy(() => import('./pages/AddressDetailPage').then(m => ({ default: m.AddressDetailPage })));
const LeadsPage = React.lazy(() => import('./pages/LeadsPage'));
const LeadReportsPage = React.lazy(() => import('./pages/LeadReportsPage'));
const DealmakerAuswertungPage = React.lazy(() => import('./pages/DealmakerAuswertungPage'));
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const ProjectDetailPage = React.lazy(() => import('./pages/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage })));
const ExposePage = React.lazy(() => import('./pages/ExposePage'));
const ImmobilienListPage = React.lazy(() => import('./pages/ImmobilienListPage'));
const WiedervorlagenPage = React.lazy(() => import('./pages/WiedervorlagenPage'));
const TasksPage = React.lazy(() => import('./pages/TasksPage').then(m => ({ default: m.TasksPage })));
const PosteingangPage = React.lazy(() => import('./pages/PosteingangPage'));
const NotesPage = React.lazy(() => import('./pages/NotesPage'));
const LohnabrechnungPage = React.lazy(() => import('./pages/LohnabrechnungPage'));
const ContractsPage = React.lazy(() => import('./pages/ContractsPage'));
const CalendarPageV2 = React.lazy(() => import('./pages/CalendarPageMobileAware'));
const EmailsPage = React.lazy(() => import('./pages/EmailsPage').then(m => ({ default: m.EmailsPage })));
const MobileEmailPage = React.lazy(() => import('./pages/MobileEmailPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
const FamilyLoansPage = React.lazy(() => import('./pages/family/LoansPage'));
const FamilyBudgetPage = React.lazy(() => import('./pages/family/HouseholdBookPage'));
const FamilyPropertiesPage = React.lazy(() => import('./pages/family/PropertiesPage'));
const FamilySavingsRatePage = React.lazy(() => import('./pages/family/SavingsRatePage'));
const FamilyShoppingPage = React.lazy(() => import('./pages/family/ShoppingListPage'));
const FamilyRecipesPage = React.lazy(() => import('./pages/family/RecipesPage'));
const FamilyEmergencyPage = React.lazy(() => import('./pages/family/EmergencyFolderPage'));
const FamilyBeautyPage = React.lazy(() => import('./pages/family/BeautyPage'));
const StudentGradesPage = React.lazy(() => import('./pages/student/GradesPage'));
const StudentSchedulePage = React.lazy(() => import('./pages/student/SchedulePage'));
const StudentLernplanPage = React.lazy(() => import('./pages/student/LernplanPage'));
const StudentBewerbungsKompassPage = React.lazy(() => import('./pages/student/BewerbungsKompassPage'));
const StudentKiPage = React.lazy(() => import('./pages/student/StudentKiPage'));
const KiPage = React.lazy(() => import('./pages/KiPage'));
const StudentPostPage = React.lazy(() => import('./pages/student/StudentPostPage'));
const GastroPage = React.lazy(() => import('./pages/gastro/GastroPage'));
const LegalFristenPage = React.lazy(() => import('./pages/legal/FristenPage'));
const LegalRvgPage = React.lazy(() => import('./pages/legal/RvgPage'));
const LegalKiPage = React.lazy(() => import('./pages/legal/LegalKiPage'));
const LegalKollisionPage = React.lazy(() => import('./pages/legal/KollisionPage'));
const OrderDetailPage = React.lazy(() => import('./pages/OrderDetailPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ControllingPage = React.lazy(() => import('./pages/ControllingPage').then(m => ({ default: m.ControllingPage })));
const MarketingEinstieg = React.lazy(() => import('./pages/MarketingManagerPage').then((m) => ({ default: m.MarketingEinstieg })));
const FleetPage = React.lazy(() => import('./pages/FleetPage').then(m => ({ default: m.FleetPage })));
const LexofficePage = React.lazy(() => import('./pages/LexofficePage').then(m => ({ default: m.LexofficePage })));
const TimeTrackingListsPage = React.lazy(() => import('./pages/TimeTrackingListsPage'));
const CampaignsPage = React.lazy(() => import('./pages/CampaignsPage').then(m => ({ default: m.CampaignsPage })));
const CampaignDetailPage = React.lazy(() => import('./pages/CampaignDetailPage').then(m => ({ default: m.CampaignDetailPage })));
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const AIAssistantPage = React.lazy(() => import('./pages/AIAssistantPage').then(m => ({ default: m.AIAssistantPage })));
const AIPromptsPage = React.lazy(() => import('./pages/AIPromptsPage').then(m => ({ default: m.AIPromptsPage })));
const AIWorkspacePage = React.lazy(() => import('./pages/AIWorkspacePage').then(m => ({ default: m.AIWorkspacePage })));
const AIGatewayOrchestrationPage = React.lazy(() => import('./pages/AIGatewayOrchestrationPage'));
const KostenstellenReportPage = React.lazy(() => import('./pages/KostenstellenReportPage'));
const MietrenditeCalculatorPage = React.lazy(() => import('./pages/MietrenditeCalculatorPage'));
const PropertyValuationPage = React.lazy(() => import('./pages/PropertyValuationPage'));
const FinanzierungsplanerPage = React.lazy(() => import('./pages/FinanzierungsplanerPage'));
const MessagesPage = React.lazy(() => import('./pages/MessagesPage').then(m => ({ default: m.MessagesPage })));
const HRDashboardPage = React.lazy(() => import('./pages/hr/HRDashboardPage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const EventsPage = React.lazy(() => import('./pages/events/EventsPage'));
const EventDetailPage = React.lazy(() => import('./pages/events/EventDetailPage'));
const RecruiterPage = React.lazy(() => import('./pages/RecruiterPage'));
const ArticleManagementPage = React.lazy(() => import('./pages/ArticleManagementPage'));
const MembersPage = React.lazy(() => import('./pages/MembersPage'));
const SchedulePage = React.lazy(() => import('./pages/SchedulePage'));
const ShopPage = React.lazy(() => import('./pages/ShopPage'));
const TournamentsPage = React.lazy(() => import('./pages/TournamentsPage'));
const PortalAdminPage = React.lazy(() => import('./pages/PortalAdminPage'));
const JobBoardPage = React.lazy(() => import('./pages/JobBoardPage'));
const SocialMediaPage = React.lazy(() => import('./pages/SocialMediaPage'));
const OrderboardPage = React.lazy(() => import('./pages/OrderboardPage'));
const MobileOrderboardPage = React.lazy(() => import('./pages/MobileOrderboardPage'));
const MobileMeinStandortPage = React.lazy(() => import('./pages/MobileMeinStandortPage'));
const MobileBuchhaltungPage = React.lazy(() => import('./pages/MobileBuchhaltungPage'));
const MobileDepartmentMembersPage = React.lazy(() => import('./pages/MobileDepartmentMembersPage'));
const MobileBuchhaltungProjectPage = React.lazy(() => import('./pages/MobileBuchhaltungProjectPage'));
const MobileBuchhaltungBelegePage = React.lazy(() => import('./pages/MobileBuchhaltungBelegePage'));
const MobileBuchhaltungBelegDetailPage = React.lazy(() => import('./pages/MobileBuchhaltungBelegDetailPage'));
const MobileBuchungsmaskePage = React.lazy(() => import('./pages/MobileBuchungsmaskePage'));
const CalculationTemplatesPage = React.lazy(() => import('./pages/CalculationTemplatesPage'));
const BookingPage = React.lazy(() => import('./pages/BookingPage'));
const WorkflowsPage = React.lazy(() => import('./pages/WorkflowsPage'));
const AccountingPage = React.lazy(() => import('./pages/AccountingPage'));
const InvoiceCreatePage = React.lazy(() => import('./pages/InvoiceCreatePage'));
const TeamCatalogPage = React.lazy(() => import('./pages/TeamCatalogPage'));
const TeamDashboardPage = React.lazy(() => import('./pages/TeamDashboardPage'));
const TeamCommPage = React.lazy(() => import('./pages/TeamCommPage'));
const EasyWorkChat = React.lazy(() => import('./pages/EasyWorkChat'));
const ConsultingPage = React.lazy(() => import('./pages/ConsultingPage'));
const KrisenManagerPage = React.lazy(() => import('./pages/KrisenManagerPage'));
const BusinessplanPage = React.lazy(() => import('./pages/BusinessplanPage'));
const TeamOrgChartPage = React.lazy(() => import('./pages/TeamOrgChartPage'));
const MeinErfolgsteamPage = React.lazy(() => import('./pages/MeinErfolgsteamPage'));

// ─── Loading Fallback ───
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-500">Laden...</span>
      </div>
    </div>
  );
}

// Error Boundary – fängt Render-Crashes ab und zeigt im Dev-Mode
// Component + Stack + Copy-to-Clipboard. Chunk-Lade-Fehler nach einem Deployment
// zeigen stattdessen „Neue Version“ und laden automatisch neu, sobald der neue
// Build ausgeliefert wird (utils/versionReload.js).
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, showDetails: false, copied: false, isChunkError: false, autoReloading: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunkError: isChunkLoadError(error), autoReloading: isChunkLoadError(error) && canAutoReload() };
  }
  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      this.setState({ autoReloading: reloadWhenReady() });
      return;
    }
    // Stack-Info für Anzeige + Reporting speichern
    this.setState({ info });
    // Eindeutiges Logging mit Route + Zeitstempel
    console.error(
      '[ErrorBoundary]',
      new Date().toISOString(),
      window.location.pathname,
      error,
      info?.componentStack
    );
  }
  buildReport() {
    const { error, info } = this.state;
    return [
      `Zeitpunkt: ${new Date().toISOString()}`,
      `Route:     ${window.location.pathname}${window.location.search}`,
      `User-Agent: ${navigator.userAgent}`,
      `Viewport:  ${window.innerWidth}x${window.innerHeight}`,
      ``,
      `Fehler: ${error?.name || 'Error'}: ${error?.message || ''}`,
      ``,
      `Stack:`,
      error?.stack || '(kein Stack)',
      ``,
      `Component-Stack:`,
      info?.componentStack || '(nicht verfügbar)',
    ].join('\n');
  }
  copyReport = async () => {
    try {
      await navigator.clipboard.writeText(this.buildReport());
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    } catch {
      // ignore
    }
  };
  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.isChunkError) return <NewVersionNotice autoReloading={this.state.autoReloading} />;

    const { error, info, showDetails, copied } = this.state;
    const isDev = process.env.NODE_ENV !== 'production';
    // Komponenten-Name aus dem ersten Eintrag des Component-Stacks extrahieren
    const componentName = (() => {
      const stack = info?.componentStack || '';
      const m = stack.match(/\s*at\s+([A-Za-z0-9_]+)|\s*in\s+([A-Za-z0-9_]+)/);
      return m?.[1] || m?.[2] || null;
    })();

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden" data-testid="error-boundary">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 text-white">
            <div className="flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <div>
                <h1 className="text-lg font-bold">Etwas ist schiefgelaufen</h1>
                <p className="text-sm text-white/90">Die Komponente konnte nicht gerendert werden.</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-1">Fehler</p>
              <p className="text-sm text-red-900 font-mono break-words" data-testid="error-message">
                {error?.name || 'Error'}: {error?.message || 'Unbekannter Fehler'}
              </p>
              {componentName && (
                <p className="text-xs text-red-700 mt-2">
                  betroffene Komponente: <span className="font-mono font-semibold">{componentName}</span>
                </p>
              )}
              <p className="text-xs text-red-700 mt-1">
                Route: <span className="font-mono">{window.location.pathname}</span>
              </p>
            </div>

            {(isDev || showDetails) && (
              <div className="space-y-2">
                {error?.stack && (
                  <details open={isDev} className="bg-gray-900 text-gray-100 rounded-lg overflow-hidden">
                    <summary className="cursor-pointer px-3 py-2 text-xs font-semibold bg-gray-800 select-none">
                      Stack-Trace
                    </summary>
                    <pre className="text-[11px] leading-relaxed p-3 overflow-auto max-h-56 font-mono whitespace-pre-wrap break-words" data-testid="error-stack">
{error.stack}
                    </pre>
                  </details>
                )}
                {info?.componentStack && (
                  <details className="bg-gray-900 text-gray-100 rounded-lg overflow-hidden">
                    <summary className="cursor-pointer px-3 py-2 text-xs font-semibold bg-gray-800 select-none">
                      Component-Stack
                    </summary>
                    <pre className="text-[11px] leading-relaxed p-3 overflow-auto max-h-56 font-mono whitespace-pre-wrap break-words" data-testid="error-component-stack">
{info.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                data-testid="error-reload-btn"
              >
                Seite neu laden
              </button>
              <button
                onClick={() => { window.location.href = '/dashboard'; }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                data-testid="error-home-btn"
              >
                Zum Dashboard
              </button>
              {!isDev && (
                <button
                  onClick={() => this.setState({ showDetails: !showDetails })}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  data-testid="error-toggle-details-btn"
                >
                  {showDetails ? 'Details ausblenden' : 'Technische Details'}
                </button>
              )}
              <button
                onClick={this.copyReport}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
                data-testid="error-copy-btn"
              >
                {copied ? 'Kopiert ✓' : 'Fehler-Report kopieren'}
              </button>
            </div>

            <p className="text-xs text-gray-500 pt-2">
              Sende den Fehler-Report an deinen Administrator, damit das Problem schneller behoben werden kann.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

// Global context menu disabler
function useDisableContextMenu() {
  useEffect(() => {
    const handleContextMenu = (e) => {
      const target = e.target;
      const isInputOrTextarea = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      // Rich-Text-Editoren (TipTap/contenteditable): Browser-Menü mit „Einfügen“ zulassen
      const isEditable = !!target.isContentEditable;
      const hasCustomContextMenu = target.closest('[data-context-menu="true"]');
      
      if (!isInputOrTextarea && !isEditable && !hasCustomContextMenu) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);
}

// Protected Route Wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  useDisableContextMenu();
  const [searchParams] = useSearchParams();
  // Wird gesetzt, wenn die Seite in einem Dashboard-Iframe eingebettet ist
  // (siehe DashboardShell.jsx) — dann keine eigene Sidebar/Header/TabBar rendern,
  // da diese bereits vom umgebenden Dashboard gestellt werden.
  const isEmbedded = searchParams.get('embedded') === '1';

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isEmbedded) {
    return <div className="p-3 sm:p-4 lg:p-6">{children}</div>;
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <ChunkErrorBoundary>
      <Routes>
        {/* Öffentliche Website (live seit 11.10.2026); bisherige Startseite bleibt unter /alt erreichbar */}
        <Route path="/" element={<NeuStartseite />} />
        <Route path="/preise" element={<NeuPreise />} />
        <Route path="/branchen/:slug" element={<NeuBranche />} />
        <Route path="/alt" element={<LandingPage />} />
        {/* Frühere Vorschau-Adressen bleiben gültig (geteilte Links) */}
        <Route path="/neu" element={<NeuStartseite />} />
        <Route path="/neu/preise" element={<NeuPreise />} />
        <Route path="/neu/branchen/:slug" element={<NeuBranche />} />
        
        {/* Login Route */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
        />

        {/* Register Route */}
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />}
        />

        {/* Demo Route */}
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/mitglieder-plus/fussball" element={<FussballLandingpage />} />
        <Route path="/branchen/student" element={<StudentBranchenseite />} />

        {/* Legal Pages */}
        <Route path="/impressum" element={<ImprintPage />} />
        <Route path="/datenschutz" element={<PrivacyPage />} />
        <Route path="/agb" element={<TermsPage />} />
        <Route path="/av-vertrag" element={<AVVPage />} />

        {/* Password Reset Routes (public) */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Super-Admin Routes */}
        <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
        <Route path="/superadmin/reset-password" element={<SuperAdminResetPasswordPage />} />
        <Route path="/superadmin" element={<SuperAdminPageMobileAware />} />

        {/* Portal Routes */}
        <Route path="/portal/:tenantSlug/*" element={<PortalLayout />} />

        {/* Public Booking Routes */}
        <Route path="/book/:userSlug" element={<PublicBookingPage />} />
        <Route path="/book/:userSlug/:eventSlug" element={<PublicBookingPage />} />

        {/* Public Restaurant Reservation Widget */}
        <Route path="/reservieren/:slug" element={<PublicReservationPage />} />

        {/* Public Speisekarte (Phase 4: QR-Code-Landing) */}
        <Route path="/karte/:slug" element={<PublicMenuPage />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/addresses" element={<ProtectedRoute><AddressesPageMobileAware /></ProtectedRoute>} />
        <Route path="/papierkorb" element={<ProtectedRoute><AdressenPapierkorbPage /></ProtectedRoute>} />
        <Route path="/projects/papierkorb" element={<ProtectedRoute><AdressenPapierkorbPage standardBereich="projekte" /></ProtectedRoute>} />
        <Route path="/addresses/papierkorb" element={<ProtectedRoute><AdressenPapierkorbPage /></ProtectedRoute>} />
        <Route path="/addresses/:id" element={<ProtectedRoute><AddressDetailPage /></ProtectedRoute>} />
        <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
        <Route path="/leads/reports" element={<ProtectedRoute><LeadReportsPage /></ProtectedRoute>} />
        <Route path="/leads/dealmaker-auswertung" element={<ProtectedRoute><DealmakerAuswertungPage /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
        <Route path="/projects/:id/expose" element={<ProtectedRoute><ExposePage /></ProtectedRoute>} />
        <Route path="/immobilien" element={<ProtectedRoute><ImmobilienListPage /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
        <Route path="/events/:eventId" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
        <Route path="/wiedervorlagen" element={<ProtectedRoute><WiedervorlagenPage /></ProtectedRoute>} />
        <Route path="/posteingang" element={<ProtectedRoute><PosteingangPage /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
        <Route path="/payroll" element={<ProtectedRoute><LohnabrechnungPage /></ProtectedRoute>} />
        <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CalendarPageV2 /></ProtectedRoute>} />
        <Route path="/emails" element={<ProtectedRoute><EmailsPage /></ProtectedRoute>} />
        <Route path="/emails-mobile" element={<ProtectedRoute><MobileEmailPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/family/loans" element={<ProtectedRoute><FamilyLoansPage /></ProtectedRoute>} />
        <Route path="/family/budget" element={<ProtectedRoute><FamilyBudgetPage /></ProtectedRoute>} />
        <Route path="/family/shopping" element={<ProtectedRoute><FamilyShoppingPage /></ProtectedRoute>} />
        <Route path="/family/recipes" element={<ProtectedRoute><FamilyRecipesPage /></ProtectedRoute>} />
        <Route path="/family/emergency" element={<ProtectedRoute><FamilyEmergencyPage /></ProtectedRoute>} />
        <Route path="/family/beauty" element={<ProtectedRoute><FamilyBeautyPage /></ProtectedRoute>} />
        <Route path="/student/grades" element={<ProtectedRoute><StudentGradesPage /></ProtectedRoute>} />
        <Route path="/student/schedule" element={<ProtectedRoute><StudentSchedulePage /></ProtectedRoute>} />
        <Route path="/student/studyplan" element={<ProtectedRoute><StudentLernplanPage /></ProtectedRoute>} />
        <Route path="/student/bewerbung" element={<ProtectedRoute><StudentBewerbungsKompassPage /></ProtectedRoute>} />
        <Route path="/student/ki" element={<ProtectedRoute><StudentKiPage /></ProtectedRoute>} />
        <Route path="/ki" element={<ProtectedRoute><KiPage /></ProtectedRoute>} />
        <Route path="/student/post" element={<ProtectedRoute><StudentPostPage /></ProtectedRoute>} />
        <Route path="/family/properties" element={<ProtectedRoute><FamilyPropertiesPage /></ProtectedRoute>} />
        <Route path="/gastro" element={<ProtectedRoute><GastroPage /></ProtectedRoute>} />
        <Route path="/legal/fristen" element={<ProtectedRoute><LegalFristenPage /></ProtectedRoute>} />
        <Route path="/legal/rvg" element={<ProtectedRoute><LegalRvgPage /></ProtectedRoute>} />
        <Route path="/legal/ki" element={<ProtectedRoute><LegalKiPage /></ProtectedRoute>} />
        <Route path="/legal/kollision" element={<ProtectedRoute><LegalKollisionPage /></ProtectedRoute>} />
        <Route path="/family/savings-rate" element={<ProtectedRoute><FamilySavingsRatePage /></ProtectedRoute>} />
        <Route path="/articles" element={<Navigate to="/settings?tab=articles" replace />} />
        <Route path="/members" element={<ProtectedRoute><MembersPage /></ProtectedRoute>} />
        <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><Navigate to="/members?tab=shop" replace /></ProtectedRoute>} />
        <Route path="/tournaments" element={<ProtectedRoute><Navigate to="/members?tab=tournaments" replace /></ProtectedRoute>} />
        <Route path="/portal-admin" element={<ProtectedRoute><Navigate to="/members?tab=portal" replace /></ProtectedRoute>} />
        <Route path="/job-board" element={<ProtectedRoute><JobBoardPage /></ProtectedRoute>} />
        <Route path="/orderboard" element={<ProtectedRoute><Navigate to="/gastro?tab=orderboard" replace /></ProtectedRoute>} />
        <Route path="/m/orderboard" element={<MobileOrderboardPage />} />
        <Route path="/m/orderboard/mein-standort" element={<ProtectedRoute><MobileMeinStandortPage /></ProtectedRoute>} />
        <Route path="/m/buchhaltung" element={<ProtectedRoute><MobileBuchhaltungPage /></ProtectedRoute>} />
        <Route path="/m/mitglieder/standort/:bereichId" element={<ProtectedRoute><MobileDepartmentMembersPage /></ProtectedRoute>} />
        <Route path="/m/mitglieder/:departmentId" element={<ProtectedRoute><MobileDepartmentMembersPage /></ProtectedRoute>} />
        <Route path="/m/buchhaltung/:projectId" element={<ProtectedRoute><MobileBuchhaltungProjectPage /></ProtectedRoute>} />
        <Route path="/m/buchhaltung/:projectId/belege" element={<ProtectedRoute><MobileBuchhaltungBelegePage /></ProtectedRoute>} />
        <Route path="/m/buchhaltung/:projectId/belege/:belegId" element={<ProtectedRoute><MobileBuchhaltungBelegDetailPage /></ProtectedRoute>} />
        <Route path="/m/buchhaltung/:projectId/belege/:belegId/buchen" element={<ProtectedRoute><MobileBuchungsmaskePage /></ProtectedRoute>} />
        <Route path="/social-media" element={<ProtectedRoute><SocialMediaPage /></ProtectedRoute>} />
        <Route path="/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
        <Route path="/invoices/new" element={<ProtectedRoute><InvoiceCreatePage /></ProtectedRoute>} />
        <Route path="/invoices/edit/:id" element={<ProtectedRoute><InvoiceCreatePage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/settings/ai-gateway" element={<ProtectedRoute><AIGatewayOrchestrationPage /></ProtectedRoute>} />
        <Route path="/finanzmanager/kostenstellen" element={<ProtectedRoute><KostenstellenReportPage /></ProtectedRoute>} />
        <Route path="/finanzmanager/mietrendite" element={<ProtectedRoute><MietrenditeCalculatorPage /></ProtectedRoute>} />
        <Route path="/finanzmanager/immobilienbewertung" element={<ProtectedRoute><PropertyValuationPage /></ProtectedRoute>} />
        <Route path="/finanzmanager/immobilienbewertung/:id" element={<ProtectedRoute><PropertyValuationPage /></ProtectedRoute>} />
        <Route path="/finanzmanager/finanzierungsplaner" element={<ProtectedRoute><FinanzierungsplanerPage /></ProtectedRoute>} />
        <Route path="/team-catalog" element={<ProtectedRoute><TeamCatalogPage /></ProtectedRoute>} />
        <Route path="/team-dashboard" element={<ProtectedRoute><TeamDashboardPage /></ProtectedRoute>} />
        <Route path="/teamcomm" element={<ProtectedRoute><TeamCommPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><EasyWorkChat /></ProtectedRoute>} />
        <Route path="/chat/:roomId" element={<ProtectedRoute><EasyWorkChat /></ProtectedRoute>} />
        <Route path="/controlling" element={<ProtectedRoute><ControllingPage /></ProtectedRoute>} />
        <Route path="/marketing" element={<ProtectedRoute><MarketingEinstieg /></ProtectedRoute>} />
        <Route path="/marketing/:projectId" element={<ProtectedRoute><MarketingEinstieg /></ProtectedRoute>} />
        <Route path="/fleet" element={<ProtectedRoute><FleetPage /></ProtectedRoute>} />
        <Route path="/recruiting" element={<ProtectedRoute><RecruiterPage /></ProtectedRoute>} />
        <Route path="/hr" element={<ProtectedRoute><HRDashboardPage /></ProtectedRoute>} />
        <Route path="/lexoffice" element={<ProtectedRoute><LexofficePage /></ProtectedRoute>} />
        <Route path="/timetracking-lists" element={<ProtectedRoute><TimeTrackingListsPage /></ProtectedRoute>} />
        <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
        <Route path="/campaigns/:id" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
        <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
        <Route path="/ai-workspace" element={<ProtectedRoute><AIWorkspacePage /></ProtectedRoute>} />
        <Route path="/ai-prompts" element={<ProtectedRoute><AIPromptsPage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/messages/:conversationId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/booking" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
        <Route path="/calculation-templates" element={<ProtectedRoute><CalculationTemplatesPage /></ProtectedRoute>} />
        <Route path="/workflows" element={<ProtectedRoute><WorkflowsPage /></ProtectedRoute>} />
        <Route path="/accounting" element={<ProtectedRoute><AccountingPage /></ProtectedRoute>} />
        <Route path="/consulting" element={<ProtectedRoute><ConsultingPage /></ProtectedRoute>} />
        <Route path="/krisenmanager" element={<ProtectedRoute><KrisenManagerPage /></ProtectedRoute>} />
        <Route path="/consulting/businessplan" element={<ProtectedRoute><BusinessplanPage /></ProtectedRoute>} />
        <Route path="/teams/organigram" element={<ProtectedRoute><TeamOrgChartPage /></ProtectedRoute>} />
        <Route path="/erfolgsteam" element={<ProtectedRoute><MeinErfolgsteamPage /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchResultsPage /></ProtectedRoute>} />

        {/* Documentation Download */}
        <Route path="/documentation" element={<DocumentationLoginPage />} />

        {/* Public Tournament Pages */}
        <Route path="/t/:slug" element={<PublicTournamentPage />} />
        <Route path="/scoreboard/:slug" element={<TournamentScoreboard />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ChunkErrorBoundary>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TabProvider>
          <WindowManagerProvider>
            <EmailComposerProvider>
              <ChatProvider>
                <AufgabenFensterProvider>
                <TerminFensterProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
                <CookieConsentBanner />
                <DeployOverlay />
                <NewVersionNotice listen />
                <SitzungGewechseltHinweis />
                <AufgabenFensterAnzeige />
                <TerminFensterAnzeige />
                </TerminFensterProvider>
                </AufgabenFensterProvider>
              </ChatProvider>
            </EmailComposerProvider>
          </WindowManagerProvider>
        </TabProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
